import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTours } from '../services/api';
import { MapPin, Clock, Globe, Plane, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { tourCurrencySymbol } from '@/lib/utils';
import { TourDateBadge } from '../components/TourDateBadge';

export default function ToursPage() {
  const { language, t } = useLanguage();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const params = selectedType !== 'all' ? { tour_type: selectedType, lang: language } : { lang: language };
        const response = await getTours(params);
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [selectedType, language]);

  const tourTypes = [
    { value: 'all', label: 'Tüm Turlar', icon: Globe, color: 'orange' },
    { value: 'istanbul', label: 'İstanbul Turları', icon: Building2, color: 'blue' },
    { value: 'yurtici', label: 'Yurtiçi Turlar', icon: MapPin, color: 'green' },
    { value: 'yurtdisi', label: 'Yurtdışı Turlar', icon: Plane, color: 'purple' },
  ];

  const getTypeColor = (type) => {
    switch(type) {
      case 'istanbul': return 'bg-blue-100 text-blue-700';
      case 'yurtici': return 'bg-green-100 text-green-700';
      case 'yurtdisi': return 'bg-purple-100 text-purple-700';
      default: return 'bg-orange-100 text-orange-700';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'istanbul': return 'İstanbul';
      case 'yurtici': return 'Yurtiçi';
      case 'yurtdisi': return 'Yurtdışı';
      default: return type;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Tüm Turlar"
        description="İstanbul, yurtiçi ve yurtdışı turlarımızı keşfedin. Profesyonel rehber eşliğinde unutulmaz tur deneyimleri."
      />
      
      {/* Header */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="tours-page-title">
            Tüm Turlarımız
          </h1>
          <p className="text-lg text-gray-300">
            İstanbul, yurtiçi ve yurtdışı turlarımızı keşfedin
          </p>
        </div>
      </section>

      {/* Tour Type Filters */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-6">
          <div className="flex flex-wrap gap-3">
            {tourTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  data-testid={`filter-${type.value}`}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-ui font-bold transition-all ${
                    selectedType === type.value
                      ? `bg-${type.color}-500 text-white shadow-lg`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon size={18} />
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-64 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">Bu kategoride henüz tur bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="tours-list">
              {tours.map((tour) => (
                <Link
                  to={`/turlar/${tour.slug}`}
                  key={tour.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl"
                  data-testid={`tour-item-${tour.slug}`}
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={tour.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
                      }}
                    />
                    <TourDateBadge tour={tour} />
                    {tour.discount_percentage > 0 && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-ui font-bold">
                        %{tour.discount_percentage} İndirim
                      </div>
                    )}
                    {/* Tour Type Badge */}
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-ui font-bold ${getTypeColor(tour.tour_type)}`}>
                      {getTypeLabel(tour.tour_type)}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <MapPin size={16} />
                      <span>{tour.category}</span>
                      <span className="text-gray-300">•</span>
                      <Clock size={16} />
                      <span>{tour.duration}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {tour.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{tour.short_description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold font-ui text-orange-500">
                          {tourCurrencySymbol(tour)}{tour.price}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">/kişi</span>
                      </div>
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui">
                        Detay
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
